import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface PlayerReport {
  id: string;
  reporter_email: string;
  reported_player_id: string;
  reason: string;
  description: string | null;
  game_session_id: string | null;
  status: 'pending' | 'investigating' | 'resolved' | 'dismissed';
  admin_notes: string | null;
  created_at: string;
  resolved_at: string | null;
  reported_player?: {
    username: string | null;
    display_name: string | null;
    elo_rating: number;
  };
}

export interface PlayerSanction {
  id: string;
  player_id: string;
  sanction_type: string;
  reason: string;
  evidence_summary: string | null;
  issued_by: string;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
  revoked_at: string | null;
}

export interface GameAnalysis {
  id: string;
  game_session_id: string;
  player_id: string;
  engine_correlation_score: number | null;
  avg_centipawn_loss: number | null;
  suspicious_moves_count: number;
  timing_anomaly_score: number | null;
  overall_suspicion_score: number | null;
  analysis_data: any;
  analyzed_at: string;
}

export interface FlaggedGame {
  id: string;
  game_session_id: string;
  player_id: string;
  suspicion_score: number;
  verdict: 'suspicious' | 'likely_cheating';
  flags: string[];
  auto_detected: boolean;
  status: 'pending' | 'investigating' | 'cleared' | 'confirmed';
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  player?: {
    username: string | null;
    display_name: string | null;
    elo_rating: number;
  };
}

export interface InvestigatedPlayer {
  id: string;
  username: string | null;
  display_name: string | null;
  elo_rating: number;
  games_played: number;
  games_won: number;
  created_at: string;
  sanctions: PlayerSanction[];
  gameAnalyses: GameAnalysis[];
  recentGames: any[];
}

export function useAdminInvestigation() {
  const { user } = useAuth();
  const [reports, setReports] = useState<PlayerReport[]>([]);
  const [flaggedGames, setFlaggedGames] = useState<FlaggedGame[]>([]);
  const [loading, setLoading] = useState(false);
  const [investigatedPlayer, setInvestigatedPlayer] = useState<InvestigatedPlayer | null>(null);

  // Fetch pending reports
  const fetchReports = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('player_reports')
        .select(`
          *,
          reported_player:user_profiles!reported_player_id(
            username,
            display_name,
            elo_rating
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReports((data as any) || []);
    } catch (error: any) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  // Search for a player by username
  const searchPlayer = async (query: string): Promise<any[]> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, username, display_name, elo_rating, games_played')
        .or(`username.ilike.%${query}%,display_name.ilike.%${query}%`)
        .limit(10);

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error searching players:', error);
      return [];
    }
  };

  // Investigate a specific player
  const investigatePlayer = async (playerId: string) => {
    setLoading(true);
    try {
      // Fetch player profile
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', playerId)
        .single();

      if (profileError) throw profileError;

      // Fetch sanctions
      const { data: sanctions, error: sanctionsError } = await supabase
        .from('player_sanctions')
        .select('*')
        .eq('player_id', playerId)
        .order('created_at', { ascending: false });

      if (sanctionsError) throw sanctionsError;

      // Fetch cached game analyses
      const { data: analyses, error: analysesError } = await supabase
        .from('game_analysis_cache')
        .select('*')
        .eq('player_id', playerId)
        .order('analyzed_at', { ascending: false })
        .limit(50);

      if (analysesError) throw analysesError;

      // Fetch recent games
      const { data: games, error: gamesError } = await supabase
        .from('game_sessions')
        .select('*')
        .or(`white_player_id.eq.${playerId},black_player_id.eq.${playerId}`)
        .eq('game_status', 'checkmate')
        .order('created_at', { ascending: false })
        .limit(20);

      if (gamesError) throw gamesError;

      setInvestigatedPlayer({
        ...profile,
        sanctions: sanctions || [],
        gameAnalyses: analyses || [],
        recentGames: games || []
      });
    } catch (error: any) {
      console.error('Error investigating player:', error);
      toast.error('Failed to load player data');
    } finally {
      setLoading(false);
    }
  };

  // Update report status
  const updateReportStatus = async (
    reportId: string, 
    status: 'pending' | 'investigating' | 'resolved' | 'dismissed',
    adminNotes?: string
  ) => {
    try {
      const updates: any = { status };
      if (adminNotes) updates.admin_notes = adminNotes;
      if (status === 'resolved' || status === 'dismissed') {
        updates.resolved_at = new Date().toISOString();
        updates.resolved_by = user?.id;
      }

      const { error } = await supabase
        .from('player_reports')
        .update(updates)
        .eq('id', reportId);

      if (error) throw error;
      toast.success('Report status updated');
      await fetchReports();
    } catch (error: any) {
      console.error('Error updating report:', error);
      toast.error('Failed to update report');
    }
  };

  // Add a new report (from email)
  const addReport = async (data: {
    reporter_email: string;
    reported_player_id: string;
    reason: string;
    description?: string;
    game_session_id?: string;
  }) => {
    try {
      const { error } = await supabase
        .from('player_reports')
        .insert(data);

      if (error) throw error;
      toast.success('Report added');
      await fetchReports();
    } catch (error: any) {
      console.error('Error adding report:', error);
      toast.error('Failed to add report');
    }
  };

  // Issue a sanction
  const issueSanction = async (data: {
    player_id: string;
    sanction_type: 'warning' | 'suspension' | 'ban';
    reason: string;
    evidence_summary?: string;
    expires_at?: string;
  }) => {
    try {
      const { error } = await supabase
        .from('player_sanctions')
        .insert({
          ...data,
          issued_by: user?.id
        });

      if (error) throw error;
      toast.success(`${data.sanction_type.charAt(0).toUpperCase() + data.sanction_type.slice(1)} issued successfully`);
      
      if (investigatedPlayer) {
        await investigatePlayer(investigatedPlayer.id);
      }
    } catch (error: any) {
      console.error('Error issuing sanction:', error);
      toast.error('Failed to issue sanction');
    }
  };

  // Revoke a sanction
  const revokeSanction = async (sanctionId: string, reason: string) => {
    try {
      const { error } = await supabase
        .from('player_sanctions')
        .update({
          is_active: false,
          revoked_at: new Date().toISOString(),
          revoked_by: user?.id,
          revoke_reason: reason
        })
        .eq('id', sanctionId);

      if (error) throw error;
      toast.success('Sanction revoked');
      
      if (investigatedPlayer) {
        await investigatePlayer(investigatedPlayer.id);
      }
    } catch (error: any) {
      console.error('Error revoking sanction:', error);
      toast.error('Failed to revoke sanction');
    }
  };

  // Fetch auto-flagged games
  const fetchFlaggedGames = async () => {
    try {
      const { data, error } = await supabase
        .from('flagged_games')
        .select(`
          *,
          player:user_profiles!player_id(
            username,
            display_name,
            elo_rating
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFlaggedGames((data as any) || []);
    } catch (error: any) {
      console.error('Error fetching flagged games:', error);
    }
  };

  // Update flagged game status
  const updateFlagStatus = async (
    flagId: string,
    status: 'pending' | 'investigating' | 'cleared' | 'confirmed',
    adminNotes?: string
  ) => {
    try {
      const updates: any = { 
        status,
        reviewed_by: user?.id,
        reviewed_at: new Date().toISOString()
      };
      if (adminNotes) updates.admin_notes = adminNotes;

      const { error } = await supabase
        .from('flagged_games')
        .update(updates)
        .eq('id', flagId);

      if (error) throw error;
      toast.success('Flag status updated');
      await fetchFlaggedGames();
    } catch (error: any) {
      console.error('Error updating flag:', error);
      toast.error('Failed to update flag');
    }
  };

  // Save game analysis to cache
  const saveGameAnalysis = async (data: {
    game_session_id: string;
    player_id: string;
    engine_correlation_score: number;
    avg_centipawn_loss: number;
    suspicious_moves_count: number;
    timing_anomaly_score: number;
    overall_suspicion_score: number;
    analysis_data: any;
  }) => {
    try {
      const { error } = await supabase
        .from('game_analysis_cache')
        .upsert(data, { onConflict: 'game_session_id,player_id' });

      if (error) throw error;
    } catch (error: any) {
      console.error('Error saving analysis:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchReports();
      fetchFlaggedGames();
    }
  }, [user]);

  return {
    reports,
    flaggedGames,
    loading,
    investigatedPlayer,
    fetchReports,
    fetchFlaggedGames,
    searchPlayer,
    investigatePlayer,
    updateReportStatus,
    updateFlagStatus,
    addReport,
    issueSanction,
    revokeSanction,
    saveGameAnalysis,
    clearInvestigation: () => setInvestigatedPlayer(null)
  };
}
